import type { CalloutBubble, Point2D, TextParagraph } from '../model';
import { layoutText, estimateTextWidth, type TextMeasure } from './textLayout';

/** Shared outline for live SVG, thumbnails, raster exports and PDF. The tip is part of the outline. */
export function calloutOutline(a: Point2D, b: Point2D, tip: Point2D, variant: CalloutBubble['variant']): string {
  const left = Math.min(a.x, b.x),
    top = Math.min(a.y, b.y);
  const w = Math.abs(b.x - a.x),
    h = Math.abs(b.y - a.y);
  const cx = left + w / 2,
    cy = top + h / 2;
  if (!w || !h) return '';
  if (variant === 'leader') return `M${left},${top + h} L${left + w},${top + h} L${tip.x},${tip.y}`;
  if (variant === 'chevron' || variant === 'notched-arrow') {
    const dx = tip.x - cx,
      dy = tip.y - cy;
    const horizontal = Math.abs(dx / w) >= Math.abs(dy / h);
    const u = horizontal ? { x: dx >= 0 ? 1 : -1, y: 0 } : { x: 0, y: dy >= 0 ? 1 : -1 };
    const v = { x: -u.y, y: u.x };
    const length = (horizontal ? w : h) / 2,
      breadth = (horizontal ? h : w) / 2;
    const depth = variant === 'chevron' ? Math.min(length * 0.65, breadth) : Math.min(length * 0.2, breadth * 0.3);
    const p = (along: number, across: number) => ({
      x: cx + along * u.x + across * v.x,
      y: cy + along * u.y + across * v.y
    });
    return (
      [p(-length, breadth), p(length, breadth), tip, p(length, -breadth), p(-length, -breadth), p(-length + depth, 0)]
        .map((point, i) => `${i ? 'L' : 'M'}${point.x},${point.y}`)
        .join(' ') + ' Z'
    );
  }
  const points: Point2D[] = [];
  if (variant === 'rect' || variant === 'rounded') {
    const radius = variant === 'rounded' ? Math.min(w, h) * 0.18 : 0;
    for (const [x, y, start] of [
      [left + w - radius, top + radius, -Math.PI / 2],
      [left + w - radius, top + h - radius, 0],
      [left + radius, top + h - radius, Math.PI / 2],
      [left + radius, top + radius, Math.PI]
    ] as const) {
      for (let i = 0; i <= 8; i++)
        points.push({
          x: x + radius * Math.cos(start + (i * Math.PI) / 16),
          y: y + radius * Math.sin(start + (i * Math.PI) / 16)
        });
    }
    // Subdivide straight edges so the pointer can join anywhere on a side.
    const corners = [...points];
    points.length = 0;
    for (let i = 0; i < corners.length; i++) {
      const p = corners[i]!,
        q = corners[(i + 1) % corners.length]!;
      const count = Math.max(1, Math.ceil(Math.hypot(q.x - p.x, q.y - p.y) / Math.max(4, Math.min(w, h) / 12)));
      for (let j = 0; j < count; j++)
        points.push({ x: p.x + ((q.x - p.x) * j) / count, y: p.y + ((q.y - p.y) * j) / count });
    }
  } else {
    const count = variant === 'burst' ? 48 : 144;
    for (let i = 0; i < count; i++) {
      const angle = (i * Math.PI * 2) / count;
      const r = variant === 'cloud' ? 0.92 + 0.08 * Math.cos(angle * 12) : variant === 'burst' ? (i % 2 ? 0.82 : 1) : 1;
      points.push({ x: cx + (w / 2) * Math.cos(angle) * r, y: cy + (h / 2) * Math.sin(angle) * r });
    }
  }
  let nearest = 0;
  for (let i = 1; i < points.length; i++)
    if (
      Math.hypot(points[i]!.x - tip.x, points[i]!.y - tip.y) <
      Math.hypot(points[nearest]!.x - tip.x, points[nearest]!.y - tip.y)
    )
      nearest = i;
  const outside = tip.x < left || tip.x > left + w || tip.y < top || tip.y > top + h;
  const wing = Math.max(1, Math.round(points.length * 0.025));
  const path = outside
    ? [
        ...Array.from(
          { length: points.length - wing * 2 - 1 },
          (_, i) => points[(nearest + wing + 1 + i) % points.length]!
        ),
        tip
      ]
    : points;
  return path.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' ') + ' Z';
}

export function calloutTextBounds(a: Point2D, b: Point2D, variant?: CalloutBubble['variant']) {
  const left = Math.min(a.x, b.x),
    right = Math.max(a.x, b.x),
    top = Math.min(a.y, b.y),
    bottom = Math.max(a.y, b.y);
  return variant === 'leader'
    ? {
        a: { x: left, y: bottom - Math.min(28, bottom - top) },
        b: { x: right, y: bottom - Math.min(4, (bottom - top) / 4) }
      }
    : { a: { x: left, y: top }, b: { x: right, y: bottom } };
}

/** Preserve run formatting while wrapping to the bubble's inner width. */
export function calloutTextLayout(
  paragraphs: readonly TextParagraph[],
  a: Point2D,
  b: Point2D,
  measure: TextMeasure = estimateTextWidth,
  variant?: CalloutBubble['variant']
) {
  const bounds = calloutTextBounds(a, b, variant);
  a = bounds.a;
  b = bounds.b;
  const width = Math.max(8, Math.abs(b.x - a.x) - 28);
  const wrapped: TextParagraph[] = [];
  for (const paragraph of paragraphs) {
    let runs: TextParagraph['runs'][number][] = [],
      length = 0;
    for (const run of paragraph.runs) {
      for (const char of run.text) {
        const advance = layoutText([{ runs: [{ ...run, text: char }] }], { x: 0, y: 0 }, measure).width;
        if (length + advance > width && runs.length) {
          wrapped.push({ align: 'center', runs });
          runs = [];
          length = 0;
        }
        const previous = runs.at(-1);
        if (previous && JSON.stringify({ ...previous, text: '' }) === JSON.stringify({ ...run, text: '' }))
          runs[runs.length - 1] = { ...previous, text: previous.text + char };
        else runs.push({ ...run, text: char });
        length += advance;
      }
    }
    wrapped.push({ align: 'center', runs });
  }
  const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const measured = layoutText(wrapped, { x: 0, y: 0 }, measure);
  const origin = { x: center.x - measured.width / 2, y: center.y - measured.height / 2 };
  return { origin, paragraphs: wrapped, layout: layoutText(wrapped, origin, measure) };
}
